// ============================================================================
// BEOFLOW CARD COMPONENT
// Card spec v2 (Drafted beoflow-design-system / components/06-card)
// ============================================================================

import type { ReactNode } from 'react'
import { Card, useTheme } from '@mui/material'
import type { CardProps } from '@mui/material'

interface BeoflowCardProps extends Omit<CardProps, 'variant'> {
  children: ReactNode
  /** Full 1px category border (sport / zone / status). Omit for the default fill-only card. */
  accentColor?: string
  /** @deprecated Card spec v2 has one surface — accepted and ignored. */
  variant?: 'glass' | 'default'
  type?: 'button' | 'submit' | 'reset'
}

/**
 * BeoflowCard — the single normalized card. Fill only, no border on the default
 * card: translucent beoWhite 5% on the dark canvas, paper white + hairline
 * shadow on the light canvas (both applied by the MuiCard theme override).
 * Radius/padding come from the generated card spec.
 *
 * @example
 * <BeoflowCard>
 *   <Typography>Card content</Typography>
 * </BeoflowCard>
 */
export function BeoflowCard({ children, accentColor, variant, sx, ...props }: BeoflowCardProps) {
  void variant // spec v2: single surface; legacy callers pass variant="glass"
  const theme = useTheme()
  const isLight = theme.palette.mode === 'light'
  return (
    <Card
      // The array form, so a caller's sx may be a theme function or an array:
      // spread into an object, a function sx was silently dropped.
      sx={[
        {
          ...(accentColor && {
            border: `1px solid ${accentColor}`,
            ...(isLight && { boxShadow: 'none' }),
          }),
          // Printers render a 1 CSS px hairline disproportionately bold — thin it for print.
          ...(accentColor && { '@media print': { borderWidth: '0.5px' } }),
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...props}
    >
      {children}
    </Card>
  )
}

// Convenience: category card with a full 1px accent border.
BeoflowCard.withAccent = (accentColor: string) => (props: Omit<BeoflowCardProps, 'accentColor'>) => (
  <BeoflowCard accentColor={accentColor} {...props} />
)
